import { apiClient } from "@/lib/apiClient";
import i18n from "@/utils/i18n";
import type {
  SupportTicketDetailResponse,
  SupportTicketFile,
  SupportTicketListParams,
  SupportTicketListItem,
  SupportTicketSummary,
} from "../types/support-tickets.types";

const SUPPORT_TICKETS_ENDPOINT = "/api/admin/support-tickets";

export const supportTicketsApi = {
  /**
   * GET /api/admin/support-tickets - List with pagination and filters.
   */
  getAll: async (
    params?: SupportTicketListParams
  ): Promise<{ data: SupportTicketListItem[]; total: number }> => {
    const query: Record<string, unknown> = {};
    if (params?.page != null) query.page = params.page;
    if (params?.limit != null) query.limit = params.limit;
    if (params?.search) query.search = params.search;
    if (params?.status != null) query.status = params.status;
    if (params?.educationId) query.educationId = params.educationId;
    if (params?.dateFrom) query.dateFrom = params.dateFrom;
    if (params?.dateTo) query.dateTo = params.dateTo;
    if (params?.sortBy) query.sortBy = params.sortBy;

    const { data } = await apiClient.get<{
      success: boolean;
      status: number;
      items?: SupportTicketListItem[];
      data?: SupportTicketListItem[];
      count: number;
      message?: string;
    }>(SUPPORT_TICKETS_ENDPOINT, { params: query });

    const list = data.items ?? data.data ?? [];
    return {
      data: list,
      total: data.count ?? 0,
    };
  },

  getById: async (id: string): Promise<SupportTicketDetailResponse> => {
    const { data } = await apiClient.get<{
      success: boolean;
      status: number;
      data: SupportTicketDetailResponse;
      message?: string;
    }>(`${SUPPORT_TICKETS_ENDPOINT}/${id}`);
    if (!data?.data) {
      throw new Error(i18n.t("supportTicketsApi.notFound"));
    }
    return data.data;
  },

  /**
   * POST /api/admin/support-tickets/{id}/reply – Tek admin yanıtı ekler.
   * Body: content ve/veya fileId (en az biri zorunlu). Zaten yanıt varsa 400.
   * Sonrasında GET .../{id} ile detayı tekrar çekin; adminReply dolu gelirse reply formu kapatılır.
   */
  postReply: async (
    id: string,
    body: { content?: string; fileId?: string }
  ): Promise<void> => {
    if (!body.content?.trim() && !body.fileId) {
      throw new Error("Cevap metni veya dosya (fileId) gerekli");
    }
    await apiClient.post(`${SUPPORT_TICKETS_ENDPOINT}/${id}/reply`, body);
  },

  /**
   * PATCH /api/admin/support-tickets/{id}/status – Durum güncelle (2 veya 3).
   */
  patchStatus: async (id: string, status: 2 | 3): Promise<void> => {
    await apiClient.patch(`${SUPPORT_TICKETS_ENDPOINT}/${id}/status`, {
      status,
    });
  },

  getStats: async (): Promise<SupportTicketSummary> => {
    const { data } = await apiClient.get<{
      success: boolean;
      status: number;
      data: SupportTicketSummary;
      message?: string;
    }>("/api/admin/support-tickets/summary");
    if (!data?.data) {
      return {
        totalCount: 0,
        pendingCount: 0,
        resolvedCount: 0,
      };
    }
    return data.data;
  },

  /**
   * Dosyayı downloadUrl ile indirir (auth gerekiyorsa apiClient, yoksa fetch kullanır).
   */
  downloadFile: async (file: SupportTicketFile): Promise<void> => {
    const url = file.downloadUrl;
    if (!url?.trim()) {
      throw new Error(i18n.t("supportTicketsApi.noDownloadLink"));
    }
    const isRelative = url.startsWith("/");
    const blob = isRelative
      ? (await apiClient.get<Blob>(url, { responseType: "blob" })).data
      : await (await fetch(url)).blob();
    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = file.originalName || "dosya";
    link.click();
    URL.revokeObjectURL(blobUrl);
  },
};
